import * as React from "react";
import { useId } from "@floating-ui/react";
import clsx from "clsx";
import { useMediaQuery } from "react-responsive";
import { Accordion } from "../accordion/accordion/accordion.mjs";
import { AccordionContent } from "../accordion/accordion-content/accordion-content.mjs";
import { AccordionHeader } from "../accordion/accordion-header/accordion-header.mjs";
import { AccordionItem } from "../accordion/accordion-item/accordion-item.mjs";
import { AccordionTitle } from "../accordion/accordion-title/accordion-title.mjs";
import { AnalyticsProvider } from "../helpers/analytics/analytics-provider/analytics-provider.mjs";
import { Anchor } from "../typography/anchor/anchor.mjs";
import { Container } from "../container/container.mjs";
import { Grid } from "../grid/grid/grid.mjs";
import { GridColumn } from "../grid/grid-column/grid-column.mjs";
import { Heading } from "../typography/heading/heading.mjs";
import "debounce";
import "tabbable";
import { useCurrentBreakPoint } from "../helpers/helper/hooks/use-current-breakpoint.mjs";
import { useIsMounted } from "../helpers/helper/hooks/use-is-mounted.mjs";
import { Icon } from "../icon/icon/icon.mjs";
import { List } from "../list/list/list.mjs";
import { ListItem } from "../list/list-item/list-item.mjs";
import { Logo } from "../logo/telia/logo.mjs";
import { DigitalHubLogo } from "../logo/digital-hub/digital-hub-logo.mjs";
import { PropsChecker } from "../helpers/props-checker/props-checker.mjs";
import { Separator } from "../separator/separator.mjs";
import { media } from "../helpers/theme-provider/theme-provider.mjs";
const Footer = (props) => /* @__PURE__ */ React.createElement(PropsChecker, { componentProps: props, componentName: "Footer", defaultValueName: "defaultOpenColumn", valueName: "openColumn", onChangeNames: "onOpenColumnChange" }, /* @__PURE__ */ React.createElement(FooterInner, { ...props }));
const FooterInner = (props) => {
  const {
    theme = "dark",
    color,
    align = "left",
    logo,
    digitalHubLogo,
    bottomLinks,
    footer,
    socialMediaItems,
    columns,
    openColumn,
    onOpenColumnChange,
    defaultOpenColumn,
    analyticsGroup = "Footer",
    headingLevel = 2
  } = props;
  const [openColumns, setOpenColumns] = React.useState(defaultOpenColumn || []);
  const currentBreak = useCurrentBreakPoint();
  const isMounted = useIsMounted();
  const isMobileQuery = useMediaQuery({ maxWidth: media.width.sm.max });
  const isMobile = isMounted ? isMobileQuery : false;
  const columnHeadingLevel = logo ? headingLevel + 1 : headingLevel;
  const headingId = useId();
  const currentOpenColumn = onOpenColumnChange && typeof openColumn !== "undefined" ? openColumn : openColumns;
  const onTitleClick = (clickedColumnIds) => {
    if (openColumn === void 0) {
      setOpenColumns(clickedColumnIds);
    }
    onOpenColumnChange == null ? void 0 : onOpenColumnChange(clickedColumnIds);
  };
  const getList = (items, columnId, isTwoColumn) => /* @__PURE__ */ React.createElement(List, { type: "unstyled", "aria-labelledby": "".concat(columnId, "-title"), className: isTwoColumn ? "footer__list--two-column" : void 0 }, items.map((item, index) => /* @__PURE__ */ React.createElement(ListItem, { key: index }, /* @__PURE__ */ React.createElement(Anchor, { ...item, isUnderlined: false, className: "footer__link" }, item.label))));
  const getFooter = (footer2) => {
    const getfooterItemWidth = () => {
      if (bottomLinks) {
        return isMobile ? [] : ["xs"];
      }
    };
    return /* @__PURE__ */ React.createElement(GridColumn, { width: getfooterItemWidth() }, /* @__PURE__ */ React.createElement(List, { direction: isMobile ? void 0 : "inline", type: "unstyled", bordered: !isMobile }, footer2.map((item, index) => {
      return /* @__PURE__ */ React.createElement(ListItem, { className: clsx("footer__footer-item", item.className), key: index }, item.content);
    })));
  };
  const getFooterLinks = (links) => {
    return /* @__PURE__ */ React.createElement(GridColumn, { width: ["xs"] }, /* @__PURE__ */ React.createElement(List, { direction: isMobile ? void 0 : "inline", type: "unstyled" }, links.map((item, index) => {
      return /* @__PURE__ */ React.createElement(ListItem, { key: index }, /* @__PURE__ */ React.createElement(Anchor, { ...item, isUnderlined: false, isTextColor: true, className: "text-bold" }, item.label));
    })));
  };
  const getSocialMediaLinks = (items) => {
    return items.map((item, index) => {
      return /* @__PURE__ */ React.createElement(GridColumn, { key: index, width: ["xs"], align: ["middle-xs"] }, /* @__PURE__ */ React.createElement(Anchor, { ...item, className: "footer__link footer__link--social", isUnderlined: false, "aria-label": item.label }, /* @__PURE__ */ React.createElement(Icon, { modifiers: item.icon, size: "xs", align: "middle" })));
    });
  };
  const getFooterTop = () => {
    const getSpacing = () => {
      switch (currentBreak) {
        case "xs":
          return columns ? "xsmall" : "medium-plus";
        case "sm":
          return "small";
        default:
          return "medium";
      }
    };
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Grid, { align: ["between-xs"], className: "footer__top", verticalGutter: "medium" }, /* @__PURE__ */ React.createElement(GridColumn, { width: ["xs"], align: ["middle-xs"] }, logo && /* @__PURE__ */ React.createElement(Heading, { level: headingLevel, id: headingId }, /* @__PURE__ */ React.createElement(Logo, { ...logo, size: "small" }))), /* @__PURE__ */ React.createElement(GridColumn, { width: ["xs"], align: ["middle-xs"] }, /* @__PURE__ */ React.createElement(Grid, null, socialMediaItems && getSocialMediaLinks(socialMediaItems), digitalHubLogo && /* @__PURE__ */ React.createElement(GridColumn, { width: ["xs"], align: ["middle-xs"] }, /* @__PURE__ */ React.createElement(DigitalHubLogo, { ...digitalHubLogo, color: "neutral", size: "small" }))))), /* @__PURE__ */ React.createElement(Separator, { spacing: getSpacing(), weight: "thin", color: isMobile && columns ? "invisible" : "medium" }));
  };
  const getFooterBody = (columns2) => /* @__PURE__ */ React.createElement(React.Fragment, null, isMobile ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Accordion, { headingLevel: columnHeadingLevel, fullWidth: true, openItem: currentOpenColumn, onToggle: onTitleClick }, columns2.map((column) => {
    return /* @__PURE__ */ React.createElement(AccordionItem, { id: column.id, key: column.id }, /* @__PURE__ */ React.createElement(AccordionHeader, null, /* @__PURE__ */ React.createElement(AccordionTitle, { className: "footer__title h5" }, column.title)), /* @__PURE__ */ React.createElement(AccordionContent, null, getList(column.items, column.id)));
  })), /* @__PURE__ */ React.createElement(Separator, { color: "invisible", spacing: "xsmall" })) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Grid, { verticalGutter: "none", horizontalGutter: "medium" }, columns2.map((column, index) => {
    return /* @__PURE__ */ React.createElement(GridColumn, { width: column.isTwoColumn ? ["md-6"] : ["md-3"], key: index }, /* @__PURE__ */ React.createElement(Heading, { level: columnHeadingLevel, className: "footer__title h5", id: "".concat(column.id, "-title") }, column.title), /* @__PURE__ */ React.createElement("div", { className: "footer__content" }, getList(column.items, column.id, column.isTwoColumn)));
  })), /* @__PURE__ */ React.createElement(Separator, { weight: "thin", color: "medium", spacing: "medium" })));
  return /* @__PURE__ */ React.createElement(AnalyticsProvider, { group: analyticsGroup }, /* @__PURE__ */ React.createElement(
    "footer",
    {
      className: clsx("footer", "footer--align-".concat(align), {
        ["footer--".concat(color)]: !!color
      }),
      "data-theme": theme,
      "aria-labelledby": logo ? headingId : void 0
    },
    /* @__PURE__ */ React.createElement(Container, null, (logo || socialMediaItems || digitalHubLogo) && getFooterTop(), columns && getFooterBody(columns), /* @__PURE__ */ React.createElement("div", { className: "footer__footer" }, /* @__PURE__ */ React.createElement(Grid, { align: ["between-xs"], verticalGutter: "small" }, footer && getFooter(footer), isMobile && /* @__PURE__ */ React.createElement(GridColumn, null, /* @__PURE__ */ React.createElement(Separator, { color: "invisible" })), bottomLinks && getFooterLinks(bottomLinks))))
  ));
};
export {
  Footer
};
